import React, { useState } from "react";

import { useCtas } from "../../../../services/useCtas";

/**
 * Workstation · Ads & Creative — **step 5, CTA, in v2.**
 *
 * WAVE 3, SCREEN 5. Behaviour is `services/useCtas`, shared with v1.
 *
 * This step chooses the CTA each script is built with — the field whose absence
 * once put a hard-coded "C1" into every production's permanent name while the
 * script's real CTAs were C37/C38/C39. The sweep's findings are in the hook.
 */
export default function CtaStep({ offer, sessionId, isNewSession,
                                  sessionStartTime, angleId, refreshKey,
                                  onComplete }) {
  const C = useCtas({ offer, sessionId, isNewSession, sessionStartTime,
                      angleId, onComplete, refreshKey });

  const [customFor, setCustomFor] = useState(null);
  const [customText, setCustomText] = useState("");
  const [feedbackFor, setFeedbackFor] = useState(null);
  const [feedbackText, setFeedbackText] = useState("");

  return (
    <div className="ws-step" data-testid="ws-cta-step">
      <div className="ws-step-head">
        <div>
          <h3 className="ws-step-title">Call to action</h3>
          <p className="dim">
            Choose the CTA each script will be built with. The choice is what
            names the finished creative — without it the name falls back to a
            placeholder.
          </p>
        </div>
      </div>

      {C.loadState === "loading" ? (
        <div className="state ws-state" data-testid="ws-cta-loading">Reading scripts and CTAs…</div>
      ) : null}

      {C.loadState === "failed" ? (
        <div className="state ws-state state-error" role="alert" data-testid="ws-cta-load-failed">
          <p>Scripts and CTAs could not be read: {C.loadError}</p>
          <p className="dim">This is a failure, not an empty step.</p>
          <button type="button" className="btn-ghost btn-sm" onClick={C.retryLoad}
                  data-testid="ws-cta-retry">Try again</button>
        </div>
      ) : null}

      {C.loadState === "ok" && C.scripts.length === 0 ? (
        <div className="state ws-state" data-testid="ws-cta-empty">
          No scripts in this session yet. CTAs are chosen per script, so this
          step has nothing to offer until the Script step has produced one.
        </div>
      ) : null}

      {C.note ? (
        <p className="ws-step-note" role="status" data-testid="ws-cta-note">{C.note}</p>
      ) : null}
      {C.actionError ? (
        <p className="ws-step-note state-error" role="alert" data-testid="ws-cta-error">
          {C.actionError}
        </p>
      ) : null}

      {C.scripts.map((s) => {
        const id = s.script_id;
        const options = C.ctasFor(id);
        return (
          <section key={id} className="ws-cta-script" data-testid={`ws-cta-script-${id}`}>
            <header className="ws-cta-script-head">
              <span className="ws-script-n">{s.body_element_number || id}</span>
              <span className="dim">{s.platform}</span>
              {s.cta_element_number ? (
                <span className="ws-chip" data-testid={`ws-cta-chosen-${id}`}>
                  {s.cta_element_number} chosen
                </span>
              ) : (
                <span className="ws-chip is-warn" data-testid={`ws-cta-none-${id}`}>
                  no CTA chosen
                </span>
              )}
            </header>

            {options.length === 0 ? (
              <p className="dim" data-testid={`ws-cta-nooptions-${id}`}>
                No CTAs written for this script yet.
              </p>
            ) : (
              <ul className="ws-cta-options">
                {options.map((c) => {
                  const picked = s.cta_element_id === c.element_id;
                  return (
                    <li key={c.element_id}>
                      <button type="button"
                              className={`ws-cta-option ${picked ? "is-picked" : ""}`}
                              disabled={C.busy === c.element_id}
                              onClick={() => C.selectCta(id, c.element_id)}
                              data-testid={`ws-cta-option-${c.element_id}`}>
                        <span className="ws-cta-num dim">{c.element_number || "—"}</span>
                        <span className="ws-cta-text">{c.content}</span>
                        {picked ? <span className="ws-chip">chosen</span> : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="ws-cta-actions">
              <button type="button" className="btn-ghost btn-sm"
                      disabled={C.busy === id}
                      onClick={() => { setFeedbackFor(feedbackFor === id ? null : id); setFeedbackText(""); }}
                      data-testid={`ws-cta-generate-toggle-${id}`}>
                {feedbackFor === id ? "Cancel" : "Generate CTAs"}
              </button>
              <button type="button" className="btn-ghost btn-sm"
                      onClick={() => { setCustomFor(customFor === id ? null : id); setCustomText(""); }}
                      data-testid={`ws-cta-custom-toggle-${id}`}>
                {customFor === id ? "Cancel" : "Write my own"}
              </button>
            </div>

            {feedbackFor === id ? (
              <div className="ws-cta-drawer">
                <textarea rows={2} value={feedbackText}
                          onChange={(e) => setFeedbackText(e.target.value)}
                          placeholder="Any direction for these CTAs? Optional."
                          data-testid={`ws-cta-feedback-${id}`} />
                <button type="button" className="btn-primary btn-sm"
                        disabled={C.busy === id}
                        onClick={async () => {
                          const ok = await C.generateCtas(id, feedbackText);
                          if (ok) { setFeedbackFor(null); setFeedbackText(""); }
                        }}
                        data-testid={`ws-cta-generate-${id}`}>
                  {C.busy === id ? "Generating…" : "Generate"}
                </button>
              </div>
            ) : null}

            {customFor === id ? (
              <div className="ws-cta-drawer">
                <textarea rows={2} value={customText}
                          onChange={(e) => setCustomText(e.target.value)}
                          placeholder="Write the CTA exactly as it should read."
                          data-testid={`ws-cta-custom-input-${id}`} />
                <button type="button" className="btn-primary btn-sm"
                        disabled={C.busy === id || !customText.trim()}
                        onClick={async () => {
                          const ok = await C.addCustomCta(id, customText);
                          // ONLY on success. The old handler closed the form
                          // either way, so a refused CTA vanished exactly like
                          // a saved one.
                          if (ok) { setCustomFor(null); setCustomText(""); }
                        }}
                        data-testid={`ws-cta-custom-save-${id}`}>
                  {C.busy === id ? "Saving…" : "Add CTA"}
                </button>
              </div>
            ) : null}
          </section>
        );
      })}

      {C.hiddenOtherSession || C.hiddenUnstamped ? (
        <p className="dim ws-step-note" data-testid="ws-cta-hidden-note">
          Not shown:{" "}
          {[
            C.hiddenOtherSession ? `${C.hiddenOtherSession} script(s) from other sessions on this offer` : null,
            C.hiddenUnstamped ? `${C.hiddenUnstamped} that predate session tracking` : null,
          ].filter(Boolean).join("; ")}
          . This view is scoped to the current session.
        </p>
      ) : null}

      {C.predateNote > 0 ? (
        <p className="dim ws-step-note" data-testid="ws-cta-predate-note">
          Showing {C.predateNote} script{C.predateNote > 1 ? "s" : ""} that
          predate session tracking.
        </p>
      ) : null}

      {C.scripts.length > 0 ? (
        <div className="ws-step-foot">
          <div className="ws-cta-confirm">
            <span className="dim">
              {C.scripts.filter((s) => s.cta_element_id).length} of{" "}
              {C.scripts.length} scripts have a CTA
            </span>
            <button type="button" className="btn-primary btn-sm"
                    onClick={() => C.proceed && C.proceed()}
                    data-testid="ws-cta-continue">Continue</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
